import { Modal } from '@material-ui/core';
import { navigate, Redirect } from '@reach/router';
import { observer, useLocalStore } from 'mobx-react-lite';
import React, { useContext } from 'react';
import { AlertContext } from '../../contexts/Alert';
import { ChatsContext } from '../../contexts/Chats';
import { SaveChangesContext } from '../../contexts/SaveChanges';
import { TopicsContext } from '../../contexts/Topics';
import { UsersContext } from '../../contexts/Users';
import { db } from '../../firebase';
import useLoadingTimer from '../../hooks/useLoadingTimer.js';
import Error from '../common/Error';
import Loading from '../common/Loading';
import * as CSS from './elements/StaffModule';
import SetModuleForm from './SetModuleForm';
import ShareModuleForm from './ShareModuleForm';
import StaffModuleItemsForm from './StaffModuleItemsForm.js';

const StaffModule = observer(({ moduleId, topicId }) => {
  const topicsStore = useContext(TopicsContext);
  const usersStore = useContext(UsersContext);
  const saveChangesStore = useContext(SaveChangesContext);
  const chatsStore = useContext(ChatsContext);
  const alertStore = useContext(AlertContext);

  const store = useLocalStore(() => ({
    isEditModuleOpen: false,
    setEditModuleOpen(isOpen) {
      store.isEditModuleOpen = isOpen;
    },
    isShareModuleOpen: false,
    setShareModuleOpen(isOpen) {
      store.isShareModuleOpen = isOpen;
    },
  }));

  const module = topicsStore.getModuleById(moduleId);
  const isLoadingTimer = useLoadingTimer();

  if (!module && isLoadingTimer)
    return (
      <CSS.StaffLoadingContainer>
        <Loading />
      </CSS.StaffLoadingContainer>
    );
  if (!module) return <Error msg="Module not found" />;

  const handleLock = async (e) => {
    await db
      .collection('topics')
      .doc(topicId)
      .collection('modules')
      .doc(moduleId)
      .update({ locked: e.target.checked });
  };

  const handleDeleteModule = () => {
    const chatsWithModule = chatsStore.getChatsWithModule(moduleId);

    if (chatsWithModule.length > 0) {
      const chatList = chatsWithModule
        .map((chat) => `- ${chat.chatName}`)
        .join('\n');
      alertStore.setText({
        heading: 'Delete module',
        text: `Cannot delete modules that are currently being used in classrooms. \n\n Please remove this module from each of the following classrooms first: \n\n ${chatList}`,
        isWarning: true,
      });
      alertStore.open();
    } else {
      saveChangesStore.setText({
        heading: 'Delete module',
        message: 'Are you sure you want to delete this module?',
        leavingText: 'Yes',
        remainingText: 'No',
      });
      saveChangesStore.open(async () => {
        const learningResourcesDeletions = module.learningResources.map(
          (learningResource) =>
            db
              .collection('topics')
              .doc(topicId)
              .collection('modules')
              .doc(moduleId)
              .collection('learningResources')
              .doc(learningResource.id)
              .delete()
        );
        const taskDeletions = module.tasks.map((task) =>
          db
            .collection('topics')
            .doc(topicId)
            .collection('modules')
            .doc(moduleId)
            .collection('tasks')
            .doc(task.id)
            .delete()
        );
        await Promise.all([
          db
            .collection('topics')
            .doc(topicId)
            .collection('modules')
            .doc(moduleId)
            .delete(),
          ...learningResourcesDeletions,
          ...taskDeletions,
        ]);
        navigate('/staff/topics');
      });
    }
  };

  const loggedInUser = usersStore.getLoggedInUser();
  const createdByMatch = module.createdBy === loggedInUser.id;
  const isAdmin = loggedInUser.role === 'admin';
  const canLockModule = createdByMatch || isAdmin;

  return (
    <CSS.StaffContainerSpaceBetween data-testid="StaffModule">
      <CSS.ModuleInfoContainer>
        <CSS.StaffRow>
          <CSS.ModuleInfo>
            <CSS.ModuleImage src={module.imageURL} alt={module.moduleName} />
            <div>
              <CSS.ModuleHeader characterLength={module.moduleName.length}>
                {module.moduleName}
              </CSS.ModuleHeader>
              <CSS.ModuleCreatedBy>
                {module.createdBy &&
                  usersStore.getUserById(module.createdBy) &&
                  `Created by: ${
                    usersStore.getUserById(module.createdBy).displayName
                  }`}
              </CSS.ModuleCreatedBy>
            </div>
          </CSS.ModuleInfo>
          <CSS.AlignModuleButtons>
            <CSS.LockControl locked={module.locked}>
              {module.locked ? (
                <CSS.LockIcon style={{ fontSize: '2rem' }} />
              ) : (
                <CSS.UnlockIcon style={{ fontSize: '2rem' }} />
              )}
              {module.locked ? 'Locked' : 'Unlocked'}
              <CSS.LockSwitch
                disabled={!canLockModule}
                checked={module.locked}
                onChange={handleLock}
                inputProps={{ 'aria-label': 'locked module' }}
              />
            </CSS.LockControl>
            {module?.categories.size > 0 && (
              <CSS.SharedChip data-testid="Shared-Module">
                <CSS.ShareIcon style={{ fontSize: '1.4rem' }} />
                Shared
              </CSS.SharedChip>
            )}
            {usersStore.getLoggedInUser().categories.size > 1 && (
              <CSS.EditModuleButton
                $variant="secondary"
                onClick={() => store.setShareModuleOpen(true)}
              >
                Share
              </CSS.EditModuleButton>
            )}
            <CSS.EditModuleButton
              disabled={module.locked}
              onClick={() => store.setEditModuleOpen(true)}
            >
              Edit
            </CSS.EditModuleButton>
            <CSS.DeleteIconButton
              onClick={handleDeleteModule}
              aria-label="Delete module"
            >
              <CSS.Bin style={{ fontSize: '2.2rem' }}></CSS.Bin>
            </CSS.DeleteIconButton>
          </CSS.AlignModuleButtons>
        </CSS.StaffRow>
        <CSS.StaffRow>
          <CSS.StaffTabs>
            <CSS.StaffFilterLink
              to={`/staff/topics/${topicId}/modules/${moduleId}/learningResources`}
              isfirst
            >
              Learning
            </CSS.StaffFilterLink>
            <CSS.StaffFilterLink
              to={`/staff/topics/${topicId}/modules/${moduleId}/tasks`}
            >
              Tasks
            </CSS.StaffFilterLink>
          </CSS.StaffTabs>
        </CSS.StaffRow>
      </CSS.ModuleInfoContainer>
      <CSS.StaffRouter primary={false}>
        <Redirect
          from="/"
          to="learningResources"
          moduleId={moduleId}
          topicId={topicId}
          noThrow
        />
        <StaffModuleItemsForm
          path="/:collection"
          moduleId={moduleId}
          topicId={topicId}
        />
      </CSS.StaffRouter>
      <Modal
        data-testid="edit-module-modal"
        open={store.isEditModuleOpen}
        onClose={() => store.setEditModuleOpen(false)}
        aria-labelledby="edit-module-modal"
        aria-describedby="edit-module-modal"
      >
        <SetModuleForm
          topicId={topicId}
          moduleId={moduleId}
          closeModal={() => store.setEditModuleOpen(false)}
        />
      </Modal>
      <Modal
        data-testid="share-module-modal"
        open={store.isShareModuleOpen}
        onClose={() => store.setShareModuleOpen(false)}
        aria-labelledby="share-module-modal"
        aria-describedby="share-module-modal"
      >
        <ShareModuleForm
          topicId={topicId}
          moduleId={moduleId}
          closeModal={() => store.setShareModuleOpen(false)}
        />
      </Modal>
    </CSS.StaffContainerSpaceBetween>
  );
});

export default StaffModule;
